import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import config from "../../config";
import {
  InheritanceAndTrust,
  InheritanceAndTrustUpdateRequest,
  InheritanceAndTrustCreateRequest,
} from "../../../../api/src/types";
import { prepareHeaders } from "../utils";

interface BasePathParams {
  userId: string;
  docId: string;
}

// linter takes away this const

// eslint-disable-next-line
const inheritancesAndTrustsTagType = "inheritancesAndTrusts" as const;

export const inheritancesAndTrustsApi = createApi({
  reducerPath: "inheritancesAndTrustsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: config.api,
    prepareHeaders,
  }),
  tagTypes: [inheritancesAndTrustsTagType],
  endpoints: (builder) => ({
    getInheritanceAndTrust: builder.query<
      InheritanceAndTrust,
      BasePathParams & { id: string }
    >({
      query: ({ userId, docId, id }) => ({
        url: `users/${userId}/documents/${docId}/inheritances-trusts/${id}`,
        method: "GET",
      }),

      providesTags: (_, __, query) => [
        { type: inheritancesAndTrustsTagType, id: query.id },
      ],
    }),
    getInheritancesAndTrustsByUserId: builder.query<
      InheritanceAndTrust[],
      BasePathParams
    >({
      query: ({ userId, docId }) => {
        return `users/${userId}/documents/${docId}/inheritances-trusts`;
      },
      merge: (currentCache, newItems) => {
        const itemMap = new Map(currentCache.map((item) => [item.id, item]));
        newItems.forEach((item) => itemMap.set(item.id, item));
        return Array.from(itemMap.values());
      },

      providesTags: (result) => [
        ...(result?.map((inheritanceAndTrust) => ({
          type: inheritancesAndTrustsTagType,
          id: inheritanceAndTrust.id,
        })) ?? []),
        { type: inheritancesAndTrustsTagType, id: "LIST" },
      ],
    }),

    updateInheritanceAndTrust: builder.mutation<
      InheritanceAndTrust,
      BasePathParams & { id: string; data: InheritanceAndTrustUpdateRequest }
    >({
      query: ({ userId, docId, id, data }) => ({
        url: `users/${userId}/documents/${docId}/inheritances-trusts/${id}`,
        method: "PATCH",
        body: data,
      }),

      invalidatesTags: (_, __, { id }) => [
        { type: inheritancesAndTrustsTagType, id },
      ],
    }),

    createInheritanceAndTrust: builder.mutation<
      InheritanceAndTrust,
      BasePathParams & { data: InheritanceAndTrustCreateRequest }
    >({
      query: ({ userId, docId, data }) => ({
        url: `users/${userId}/documents/${docId}/inheritances-trusts`,
        method: "POST",
        body: data,
      }),

      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          const { data: newItem } = await queryFulfilled;
          if (newItem.userId && newItem.id && newItem.documentId) {
            dispatch(
              inheritancesAndTrustsApi.util.updateQueryData(
                "getInheritancesAndTrustsByUserId",
                { userId: newItem.userId, docId: newItem.documentId },
                (draft: InheritanceAndTrust[]) => {
                  draft.push(newItem);
                },
              ),
            );
          }
        } catch (error) {
          console.log("rtk query create inheritance and trust error", error);
        }
      },
    }),
    deleteInheritanceAndTrust: builder.mutation<
      InheritanceAndTrust,
      BasePathParams & { id: string }
    >({
      query: ({ userId, docId, id }) => ({
        url: `users/${userId}/documents/${docId}/inheritances-trusts/${id}`,
        method: "DELETE",
      }),
      async onQueryStarted(
        { userId, docId, id },
        { dispatch, queryFulfilled },
      ) {
        await queryFulfilled;
        dispatch(
          inheritancesAndTrustsApi.util.updateQueryData(
            "getInheritancesAndTrustsByUserId",
            { userId, docId },
            (draft: InheritanceAndTrust[]) => {
              return draft.filter((item) => item.id !== id);
            },
          ),
        );
      },
    }),
  }),
});

export const {
  useGetInheritanceAndTrustQuery,
  useGetInheritancesAndTrustsByUserIdQuery,
  useUpdateInheritanceAndTrustMutation,
  useCreateInheritanceAndTrustMutation,
  useDeleteInheritanceAndTrustMutation,
} = inheritancesAndTrustsApi;
